<script lang="ts" setup>
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { useModalStore } from '@/store/modal.store';
import { useTeamStore } from '@/store/team.store';
import { useSubscribersStore } from '@/store/subscribers.store';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/store/auth.store';
import { IDevice } from '@/types';
import DefaultBtn from "@/components/block/DefaultBtn.vue";

const teamStore = useTeamStore();
const modalStore = useModalStore();
const authStore = useAuthStore();
const route = useRoute();
const subscriberStore = useSubscribersStore();
const loading = ref<boolean>(false);
const activeTab = ref(1);

const props = defineProps<{
  closeSignal?: boolean
}>();

watch(() => props.closeSignal, () => {
  if (props.closeSignal) {
    close();
  }
});

const device = ref<IDevice | undefined>(
  modalStore.modalPayload.device ? { ...modalStore.modalPayload.device } : undefined
);

const addInfo1 = ref(
  !!(device.value?.notifications_name_2 || device.value?.notifications_phone_2 || device.value?.notifications_email_2)
);
const addInfo2 = ref(
  !!(device.value?.notifications_name_3 || device.value?.notifications_phone_3 || device.value?.notifications_email_3)
);

const currentSlide = ref(0);

const totalSlides = computed(() => {
  let slides = 1;
  if (addInfo1.value || device.value?.notifications_name_2 || device.value?.notifications_phone_2 || device.value?.notifications_email_2) {
    slides = 2;
  }
  if (addInfo2.value || device.value?.notifications_name_3 || device.value?.notifications_phone_3 || device.value?.notifications_email_3) {
    slides = 3;
  }
  return slides;
});

const goToSlide = (index: number) => {
  currentSlide.value = index;
};

const addContactInfo = () => {
  addInfo1.value = true;
  currentSlide.value = 1;
};

const addContactInfo1 = () => {
  addInfo2.value = true;
  currentSlide.value = 2;
};

const validateIMEI = (imei: string): boolean => {
  // IMEI should be 15 digits
  if (!/^\d{15}$/.test(imei)) {
    return false;
  }
  
  // Luhn algorithm validation for IMEI
  let sum = 0;
  let alternate = false;
  
  for (let i = imei.length - 1; i >= 0; i--) {
    let digit = parseInt(imei.charAt(i));
    
    if (alternate) {
      digit *= 2;
      if (digit > 9) {
        digit = (digit % 10) + 1;
      }
    }
    
    sum += digit;
    alternate = !alternate;
  }
  
  return sum % 10 === 0;
};

const handleSubmit = () => {
  if (device.value) {
    // Validate IMEI if provided
    if (device.value.deviceIMEI && !validateIMEI(device.value.deviceIMEI)) {
      alert('Please enter a valid 15-digit IMEI number.');
      return;
    }
    
    loading.value = true;
    subscriberStore.editDevice(device.value)
      .then(() => { modalStore.openModal('DeviceInfoUpdated'); })
      .finally(() => { loading.value = false; });
  }
};

const areYouSure = () => {
  modalStore.openModal('DeviceInfoNoUpdated');
};

// IMEI formatting - only allow digits and limit to 15 characters
watch(() => device.value?.deviceIMEI, (current, prev) => {
  if (current !== prev && device.value?.deviceIMEI) {
    const digitsOnly = device.value?.deviceIMEI.replace(/\D/g, '');
    device.value!.deviceIMEI = digitsOnly.slice(0, 15);
  }
});

// Форматування номера телефону
watch(() => device.value?.notifications_phone_1, (current, prev) => {
  if (current !== prev && device.value?.notifications_phone_1) {
    const digitsOnly = device.value?.notifications_phone_1.replace(/\D/g, '');
    device.value!.notifications_phone_1 = ('+1' + digitsOnly.replace(/^1/, '')).slice(0, 12);
  }
});

watch(() => device.value?.notifications_phone_2, (current, prev) => {
  if (current !== prev && device.value?.notifications_phone_2) {
    const digitsOnly = device.value?.notifications_phone_2.replace(/\D/g, '');
    device.value!.notifications_phone_2 = ('+1' + digitsOnly.replace(/^1/, '')).slice(0, 12);
  }
});

watch(() => device.value?.notifications_phone_3, (current, prev) => {
  if (current !== prev && device.value?.notifications_phone_3) {
    const digitsOnly = device.value?.notifications_phone_3.replace(/\D/g, '');
    device.value!.notifications_phone_3 = ('+1' + digitsOnly.replace(/^1/, '')).slice(0, 12);
  }
});

const isMobile = ref(false);
const handleResize = () => {
  isMobile.value = window.innerWidth < 992;
};

onMounted(() => {
  handleResize();
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>


<template>

	<div v-if="!isMobile">

		<div v-if="device && modalStore.isModalVisible('DeviceInfoEdit')" class="modal modal--profile modal--active">
			<div class="modal__body">
				<div class="modal__header">
					<p class="modal__subtitle">
						<span class="material-symbols-outlined">edit</span>
						<span>Device Info Edit</span>
					</p>
					<button @click="modalStore.closeModal" class="modal__close">
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>
				<form  @submit.prevent="handleSubmit">
					<div class="modal__main">
						<p class="form__title"><svg data-v-7b53e39e="" width="18" height="8" viewBox="0 0 18 8" xmlns="http://www.w3.org/2000/svg"><path data-v-7b53e39e="" d="M16 6.13492V5.05802C16 4.96822 15.9712 4.89452 15.9135 4.83682C15.8558 4.77912 15.782 4.75032 15.6923 4.75032H2.3077C2.21795 4.75032 2.14422 4.77912 2.08652 4.83682C2.02882 4.89452 1.99997 4.96822 1.99997 5.05802V6.13492H16ZM13.1884 3.25032L12.7922 2.06762C12.7666 2.00352 12.7281 1.95382 12.6768 1.91862C12.6256 1.88332 12.5679 1.86572 12.5038 1.86572H5.49612C5.43202 1.86572 5.37433 1.88332 5.32305 1.91862C5.27176 1.95382 5.2333 2.00352 5.20765 2.06762L4.8115 3.25032H13.1884ZM12.5038 0.365723C12.8922 0.365723 13.2439 0.478823 13.5587 0.705123C13.8734 0.931423 14.0942 1.23042 14.2211 1.60222L14.7691 3.25032H15.6923C16.191 3.25032 16.6169 3.42692 16.9701 3.78012C17.3234 4.13332 17.5 4.55932 17.5 5.05802V7.63492H0.5V5.05802C0.5 4.55932 0.67661 4.13332 1.02982 3.78012C1.38302 3.42692 1.80898 3.25032 2.3077 3.25032H3.23075L3.89038 1.54072C4.02756 1.19582 4.24679 0.913823 4.54808 0.694523C4.84934 0.475323 5.18908 0.365723 5.5673 0.365723H12.5038Z" fill="#64748B"></path></svg>Device Info</p>
						<div class="modal__main_wrap">
							<div class="login__item">
								<label  for="userDataName">Device Name</label>
								<input
									v-model="device.main_name"
									id="userDataName" 
									placeholder="Device Name"
									type="text" 
									class="login__password"
									>
								<span class="material-symbols-outlined login__item_ico">person</span>
							</div>
							

							<div class="login__item">
								<label for="userDataMiddleName">Device IMEI</label>
								<input 
								v-model="device.deviceIMEI"
								id="userDataMiddleName" 
								placeholder="Enter your device IMEI"
								type="text" 
								class="login__password"
								>
								<span class="material-symbols-outlined login__item_ico">barcode</span>
							</div>
							
						</div>

						<div v-if="device.deviceType !== 'single'" class="subDevice">
							<p class="form__title"><svg data-v-7b53e39e="" width="18" height="8" viewBox="0 0 18 8" xmlns="http://www.w3.org/2000/svg"><path data-v-7b53e39e="" d="M16 6.13492V5.05802C16 4.96822 15.9712 4.89452 15.9135 4.83682C15.8558 4.77912 15.782 4.75032 15.6923 4.75032H2.3077C2.21795 4.75032 2.14422 4.77912 2.08652 4.83682C2.02882 4.89452 1.99997 4.96822 1.99997 5.05802V6.13492H16ZM13.1884 3.25032L12.7922 2.06762C12.7666 2.00352 12.7281 1.95382 12.6768 1.91862C12.6256 1.88332 12.5679 1.86572 12.5038 1.86572H5.49612C5.43202 1.86572 5.37433 1.88332 5.32305 1.91862C5.27176 1.95382 5.2333 2.00352 5.20765 2.06762L4.8115 3.25032H13.1884ZM12.5038 0.365723C12.8922 0.365723 13.2439 0.478823 13.5587 0.705123C13.8734 0.931423 14.0942 1.23042 14.2211 1.60222L14.7691 3.25032H15.6923C16.191 3.25032 16.6169 3.42692 16.9701 3.78012C17.3234 4.13332 17.5 4.55932 17.5 5.05802V7.63492H0.5V5.05802C0.5 4.55932 0.67661 4.13332 1.02982 3.78012C1.38302 3.42692 1.80898 3.25032 2.3077 3.25032H3.23075L3.89038 1.54072C4.02756 1.19582 4.24679 0.913823 4.54808 0.694523C4.84934 0.475323 5.18908 0.365723 5.5673 0.365723H12.5038Z" fill="#64748B"></path></svg>Sub Device Info</p>
							<div class="modal__main_wrap">
								<div class="login__item">
									<label  for="userDataName">Sub Device 1 Name</label>
									<input
										v-model="device.main_name"
										id="userDataName" 
										placeholder="Sub Device 1 Name"
										type="text" 
										class="login__password"
										>
									<span class="material-symbols-outlined login__item_ico">person</span>
								</div>
								
								<div class="login__item">
									<label for="corporateId">Sub Device 1 ID</label>
									<input 
										v-model="authStore.subscriber.id"
										placeholder="Sub Device 1 ID"
										id="corporateId" 
										type="text" 
										class="login__password"
										readonly
										>
									<span class="material-symbols-outlined login__item_ico">fingerprint</span>
								</div>

								<div class="login__item">
									<label for="userDataMiddleName">Sub Device 2 Name</label>
									<input 
									v-model="device.main_name"
									id="userDataMiddleName" 
									placeholder="Sub Device 1 Name"
									type="text" 
									class="login__password"
									readonly
									>
									<span class="material-symbols-outlined login__item_ico">person</span>
								</div>
								
								<div class="login__item">
									<label for="userDataEmail">Sub Device 2 ID</label>
									<input
									v-model="authStore.subscriber.id"
									id="userDataEmail" 
									placeholder="Sub Device 2 ID"
									type="email" 
									class="login__password"
									readonly
									>
									<span class="material-symbols-outlined login__item_ico">fingerprint</span>
								</div>
							</div>
						</div>


						<div class="modal__main_stat">
							<p class="form__title"><span class="material-symbols-outlined">notifications</span>Notification Info</p>
							<div class="modal__main_wrap">
								<div class="login__item">
									<label for="userDataName">Name</label>
									<input 
									v-model="device.notifications_name_1"
									id="userDataName" 
									placeholder="Name"
									type="text" 
									class="login__password">
									<span class="material-symbols-outlined login__item_ico">person</span>
								</div>
								
								<div class="login__item">
									<label for="corporateId">Phone Number</label>
									<input 
									v-model="device.notifications_phone_1"
									id="corporateId" 
									placeholder="Phone Number"
									type="text" 
									class="login__password">
									<span class="material-symbols-outlined login__item_ico">phone</span>
								</div>

								<div class="login__item login__item-last">
									<label for="userDataMiddleName">Email</label>
									<input 
									v-model="device.notifications_email_1"
									id="userDataMiddleName" 
									placeholder="Email"
									type="email" 
									class="login__password">
									<span class="material-symbols-outlined login__item_ico">mail</span>
								</div>

								<div class="device__multy device__multy__btn">
									<div class="login__item login__item-last login__item-last_add">
										<button  v-if="!addInfo1" class="add__elem" type="button" @click="addContactInfo">
											<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
												<path d="M11.25 12.75H6.25C6.0375 12.75 5.85938 12.6781 5.71563 12.5343C5.57187 12.3904 5.5 12.2122 5.5 11.9997C5.5 11.7871 5.57187 11.609 5.71563 11.4654C5.85938 11.3218 6.0375 11.25 6.25 11.25H11.25V6.25C11.25 6.0375 11.3219 5.85938 11.4657 5.71563C11.6095 5.57187 11.7877 5.5 12.0003 5.5C12.2129 5.5 12.391 5.57187 12.5346 5.71563C12.6782 5.85938 12.7499 6.0375 12.7499 6.25V11.25H17.75C17.9625 11.25 18.1406 11.3219 18.2843 11.4657C18.4281 11.6095 18.5 11.7877 18.5 12.0003C18.5 12.2129 18.4281 12.391 18.2843 12.5346C18.1406 12.6782 17.9625 12.75 17.75 12.75H12.7499V17.75C12.7499 17.9625 12.6781 18.1406 12.5342 18.2843C12.3904 18.4281 12.2122 18.5 11.9997 18.5C11.787 18.5 11.609 18.4281 11.4654 18.2843C11.3218 18.1406 11.25 17.9625 11.25 17.75V12.75Z" fill="#2563EB"/>
											</svg>
											Add Contact Info 2
										</button>
									</div>
								</div>
							</div>
						</div>

						<div class="device__multy">
							<div v-if="addInfo1 || device.notifications_name_2 || device.notifications_phone_2 || device.notifications_email_2 " class="modal__main-add">
								<p class="form__title"><span class="material-symbols-outlined">notifications</span>Notification Info 2</p>
								<div class="modal__main_wrap">
									<div class="login__item">
										<label for="userDataName">Name</label>
										<input 
										v-model="device.notifications_name_2"
										id="userDataName"
										placeholder="Name"
										type="text" 
										class="login__password">
										<span class="material-symbols-outlined login__item_ico">person</span>
									</div>
									
									<div class="login__item">
										<label for="corporateId">Phone Number</label>
										<input 
										v-model="device.notifications_phone_2"
										placeholder="Phone Number"
										id="corporateId" 
										type="text" 
										class="login__password">
										<span class="material-symbols-outlined login__item_ico">phone</span>
									</div>

									<div class="login__item login__item-last">
										<label for="userDataMiddleName">Email</label>
										<input 
										v-model="device.notifications_email_2"
										id="userDataMiddleName" 
										placeholder="Email"
										type="email" 
										class="login__password">
										<span class="material-symbols-outlined login__item_ico">mail</span>
									</div>
									<div class="login__item login__item-last login__item-last_add">
									<button   v-if="!addInfo2"   class="add__elem" type="button" @click="addContactInfo1">
										<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
											<path d="M11.25 12.75H6.25C6.0375 12.75 5.85938 12.6781 5.71563 12.5343C5.57187 12.3904 5.5 12.2122 5.5 11.9997C5.5 11.7871 5.57187 11.609 5.71563 11.4654C5.85938 11.3218 6.0375 11.25 6.25 11.25H11.25V6.25C11.25 6.0375 11.3219 5.85938 11.4657 5.71563C11.6095 5.57187 11.7877 5.5 12.0003 5.5C12.2129 5.5 12.391 5.57187 12.5346 5.71563C12.6782 5.85938 12.7499 6.0375 12.7499 6.25V11.25H17.75C17.9625 11.25 18.1406 11.3219 18.2843 11.4657C18.4281 11.6095 18.5 11.7877 18.5 12.0003C18.5 12.2129 18.4281 12.391 18.2843 12.5346C18.1406 12.6782 17.9625 12.75 17.75 12.75H12.7499V17.75C12.7499 17.9625 12.6781 18.1406 12.5342 18.2843C12.3904 18.4281 12.2122 18.5 11.9997 18.5C11.787 18.5 11.609 18.4281 11.4654 18.2843C11.3218 18.1406 11.25 17.9625 11.25 17.75V12.75Z" fill="#2563EB"/>
										</svg>
										Add Contact Info 3
									</button>
								</div>
								</div>
							</div>

							<div v-if="addInfo2 || device.notifications_name_3 || device.notifications_phone_3 || device.notifications_email_3" class="modal__main-add">
								<p class="form__title"><span class="material-symbols-outlined">notifications</span>Notification Info 3</p>
								<div class="modal__main_wrap">
									<div class="login__item">
										<label for="userDataName">Name</label>
										<input 
										v-model="device.notifications_name_3"
										id="userDataName" 
										placeholder="Name"
										type="text" 
										class="login__password">
										<span class="material-symbols-outlined login__item_ico">person</span>
									</div>
									
									<div class="login__item">
										<label for="corporateId">Phone Number</label>
										<input 
										v-model="device.notifications_phone_3"
										id="corporateId" 
										placeholder="Phone Number"
										type="text" 
										class="login__password">
										<span class="material-symbols-outlined login__item_ico">phone</span>
									</div>

									<div class="login__item login__item-last">
										<label for="userDataMiddleName">Email</label>
										<input 
										v-model="device.notifications_email_3"
										id="userDataMiddleName" 
										placeholder="Email"
										type="email" 
										class="login__password">
										<span class="material-symbols-outlined login__item_ico">mail</span>
									</div>
								</div>
							</div>
						</div>
						<div class="modal__btn">
							<DefaultBtn class="btn btn--white" :neutral="true" text="Undo" @click="areYouSure" />
							<DefaultBtn type="submit" class="btn" :loading text="Save" />
						</div>
					</div>
				</form>
			</div>
		</div>

		<div v-if="modalStore.isModalVisible('DeviceInfoUpdated')" class="modal modal--profile modal--active">
			<div class="modal__body" >
				<div class="modal__header">
					<p class="modal__subtitle">
						<span class="material-symbols-outlined">edit</span>
						<span>Device Info Edit</span>
					</p>
					<button @click="modalStore.closeModal" class="modal__close">
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>
				<div class="modal__main modal__main-center">
					<svg class="modal__update_svg" width="93" height="94" viewBox="0 0 93 94" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M39.5532 57.505L28.1823 46.1341C27.5046 45.4564 26.6527 45.1096 25.6267 45.094C24.6007 45.0782 23.7331 45.425 23.024 46.1341C22.315 46.8432 21.9604 47.7029 21.9604 48.7132C21.9604 49.7234 22.315 50.5831 23.024 51.2922L36.4563 64.7245C37.3411 65.6093 38.3734 66.0517 39.5532 66.0517C40.733 66.0517 41.7652 65.6093 42.65 64.7245L69.8817 37.4928C70.5595 36.8151 70.9062 35.9633 70.9219 34.9373C70.9376 33.9112 70.5909 33.0437 69.8817 32.3346C69.1726 31.6255 68.313 31.271 67.3027 31.271C66.2924 31.271 65.4327 31.6255 64.7236 32.3346L39.5532 57.505ZM46.5082 93.5C40.0768 93.5 34.0316 92.2796 28.3727 89.8387C22.7136 87.3979 17.791 84.0854 13.605 79.9012C9.4188 75.7169 6.10477 70.7965 3.66286 65.1399C1.22096 59.4835 0 53.4396 0 47.0082C0 40.5768 1.22042 34.5316 3.66127 28.8727C6.10212 23.2136 9.41464 18.291 13.5988 14.105C17.7831 9.91881 22.7035 6.60478 28.3601 4.16287C34.0165 1.72096 40.0604 0.5 46.4918 0.5C52.9232 0.5 58.9684 1.72043 64.6273 4.16128C70.2864 6.60213 75.209 9.91464 79.395 14.0988C83.5812 18.2831 86.8952 23.2035 89.3371 28.8601C91.779 34.5165 93 40.5604 93 46.9918C93 53.4232 91.7796 59.4683 89.3387 65.1273C86.8979 70.7864 83.5854 75.709 79.4012 79.895C75.2169 84.0812 70.2965 87.3952 64.6399 89.8371C58.9835 92.279 52.9396 93.5 46.5082 93.5ZM46.5 86.158C57.4316 86.158 66.6908 82.3646 74.2777 74.7777C81.8646 67.1908 85.658 57.9316 85.658 47C85.658 36.0684 81.8646 26.8092 74.2777 19.2223C66.6908 11.6354 57.4316 7.842 46.5 7.842C35.5684 7.842 26.3092 11.6354 18.7223 19.2223C11.1354 26.8092 7.342 36.0684 7.342 47C7.342 57.9316 11.1354 67.1908 18.7223 74.7777C26.3092 82.3646 35.5684 86.158 46.5 86.158Z" fill="#2563EB"/>
					</svg>
					<p class="modal__update">Device information updated!</p>
				</div>
			</div>
		</div>

		<div v-if="modalStore.isModalVisible('DeviceInfoNoUpdated')" class="modal modal--profile modal--active">
			<div class="modal__body" >
				<div class="modal__header">
					<p class="modal__subtitle">
						<span class="material-symbols-outlined">edit</span>
						<span>Device Info Edit</span>
					</p>
					<button @click="modalStore.closeModal" class="modal__close">
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>
				<div class="modal__main modal__main-center">
					<svg class="modal__update_svg" width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M49.9429 79.0138C51.4181 79.0138 52.6642 78.5045 53.681 77.4859C54.6977 76.4673 55.206 75.2204 55.206 73.7451C55.206 72.2699 54.6967 71.0239 53.6781 70.0072C52.6595 68.9904 51.4126 68.482 49.9373 68.482C48.4621 68.482 47.2161 68.9913 46.1994 70.0099C45.1826 71.0285 44.6742 72.2754 44.6742 73.7507C44.6742 75.2259 45.1835 76.472 46.2021 77.4888C47.2207 78.5055 48.4676 79.0138 49.9429 79.0138ZM50.0084 99.0844C43.2197 99.0844 36.8387 97.7962 30.8653 95.2197C24.8919 92.6433 19.6959 89.1467 15.2773 84.7301C10.8585 80.3134 7.36041 75.1196 4.78285 69.1488C2.20529 63.1782 0.916504 56.7985 0.916504 50.0098C0.916504 43.2212 2.20473 36.8402 4.78117 30.8668C7.35762 24.8934 10.8542 19.6973 15.2708 15.2787C19.6875 10.86 24.8813 7.36188 30.852 4.78432C36.8227 2.20676 43.2024 0.917969 49.9911 0.917969C56.7797 0.917969 63.1607 2.2062 69.1341 4.78264C75.1075 7.35909 80.3036 10.8556 84.7222 15.2723C89.1409 19.689 92.639 24.8827 95.2166 30.8535C97.7941 36.8242 99.0829 43.2039 99.0829 49.9925C99.0829 56.7812 97.7947 63.1622 95.2183 69.1355C92.6418 75.109 89.1453 80.305 84.7286 84.7236C80.3119 89.1423 75.1182 92.6405 69.1474 95.218C63.1767 97.7956 56.797 99.0844 50.0084 99.0844ZM49.9997 91.3345C61.5386 91.3345 71.3122 87.3304 79.3205 79.322C87.3289 71.3137 91.3331 61.5401 91.3331 50.0012C91.3331 38.4623 87.3289 28.6887 79.3205 20.6803C71.3122 12.672 61.5386 8.66784 49.9997 8.66784C38.4608 8.66784 28.6872 12.672 20.6789 20.6803C12.6705 28.6887 8.66638 38.4623 8.66638 50.0012C8.66638 61.5401 12.6705 71.3137 20.6789 79.322C28.6872 87.3304 38.4608 91.3345 49.9997 91.3345ZM50.4182 27.8838C52.802 27.8838 54.865 28.6343 56.6071 30.1351C58.3492 31.6359 59.2203 33.5119 59.2203 35.7631C59.2203 37.6576 58.6639 39.3533 57.5511 40.8502C56.4382 42.3473 55.1665 43.7416 53.7357 45.0333C51.8602 46.6971 50.209 48.5272 48.7819 50.5238C47.355 52.5203 46.5918 54.744 46.4924 57.1949C46.4593 58.1354 46.7905 58.9236 47.486 59.5595C48.1815 60.1954 48.9929 60.5133 49.9203 60.5133C50.9138 60.5133 51.755 60.1821 52.4439 59.5197C53.1327 58.8574 53.5732 58.0459 53.7653 57.0855C54.1098 55.3103 54.8434 53.7288 55.9662 52.3412C57.0889 50.9534 58.3052 49.6338 59.615 48.3825C61.4979 46.5471 63.1415 44.5459 64.5457 42.3788C65.95 40.2116 66.6521 37.7945 66.6521 35.1272C66.6521 31.0337 65.0392 27.6704 61.8134 25.0374C58.5876 22.4044 54.8219 21.0879 50.5164 21.0879C47.4429 21.0879 44.545 21.7685 41.8226 23.1297C39.1002 24.4909 36.9805 26.4764 35.4636 29.0863C34.9933 29.8877 34.8423 30.7313 35.0107 31.617C35.179 32.5027 35.6336 33.1752 36.3743 33.6345C37.3171 34.1593 38.2853 34.3091 39.2789 34.0838C40.2724 33.8587 41.117 33.2924 41.8125 32.3849C42.8591 31.0269 44.1259 29.9373 45.613 29.116C47.1 28.2945 48.7018 27.8838 50.4182 27.8838Z" fill="#FF3F6D"/>
					</svg>

					<p class="modal__update">Are you sure?</p>
					<p class="modal__update_text">You are about to close this page without saving.</p>
					<div class="modal__update_btn">
						<button class="btn btn--white btn--border" type="button" @click="modalStore.closeModal">Close</button>
						<button class="btn" type="submit" @click="modalStore.openModal('DeviceInfoEdit')">Continue</button>
					</div>
				</div>
			</div>
		</div>

	</div>


	<div v-else class="login__wrap_mobile">
		<div v-if="device && modalStore.isModalVisible('DeviceInfoEdit')" class="modal modal--profile modal--active">
			<div class="modal__body">
				<div class="modal__header">
					<p class="modal__subtitle">
						<span class="material-symbols-outlined">edit</span>
						<span>Device Info Edit</span>
					</p>
					<button @click="modalStore.closeModal" class="modal__close">
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>
				<div class="modal__tabs">
					<a href="#" @click.prevent="activeTab = 1" :class="['modal__tabs_btn', { deviceInfo__tabs_active: activeTab === 1 }]">Device Info</a>
					<a href="#" @click.prevent="activeTab = 2" :class="['modal__tabs_btn', { deviceInfo__tabs_active: activeTab === 2 }]">Contact Info</a>
				</div>

				<div  v-show="activeTab === 1" class=" modal__main-wrap">
					<form  @submit.prevent="handleSubmit">
						<div class="modal__main">
							<p class="form__title"><svg data-v-7b53e39e="" width="18" height="8" viewBox="0 0 18 8" xmlns="http://www.w3.org/2000/svg"><path data-v-7b53e39e="" d="M16 6.13492V5.05802C16 4.96822 15.9712 4.89452 15.9135 4.83682C15.8558 4.77912 15.782 4.75032 15.6923 4.75032H2.3077C2.21795 4.75032 2.14422 4.77912 2.08652 4.83682C2.02882 4.89452 1.99997 4.96822 1.99997 5.05802V6.13492H16ZM13.1884 3.25032L12.7922 2.06762C12.7666 2.00352 12.7281 1.95382 12.6768 1.91862C12.6256 1.88332 12.5679 1.86572 12.5038 1.86572H5.49612C5.43202 1.86572 5.37433 1.88332 5.32305 1.91862C5.27176 1.95382 5.2333 2.00352 5.20765 2.06762L4.8115 3.25032H13.1884ZM12.5038 0.365723C12.8922 0.365723 13.2439 0.478823 13.5587 0.705123C13.8734 0.931423 14.0942 1.23042 14.2211 1.60222L14.7691 3.25032H15.6923C16.191 3.25032 16.6169 3.42692 16.9701 3.78012C17.3234 4.13332 17.5 4.55932 17.5 5.05802V7.63492H0.5V5.05802C0.5 4.55932 0.67661 4.13332 1.02982 3.78012C1.38302 3.42692 1.80898 3.25032 2.3077 3.25032H3.23075L3.89038 1.54072C4.02756 1.19582 4.24679 0.913823 4.54808 0.694523C4.84934 0.475323 5.18908 0.365723 5.5673 0.365723H12.5038Z" fill="#64748B"></path></svg>Device Info</p>
							<div class="modal__main_wrap">
								<div class="login__item">
									<label  for="userDataName">Device Name</label>
									<input
										v-model="device.main_name"
										id="userDataName" 
										placeholder="Device Name"
										type="text" 
										class="login__password"
										>
									<span class="material-symbols-outlined login__item_ico">person</span>
								</div>
								

								<div class="login__item">
									<label for="userDataMiddleName">Device IMEI</label>
									<input 
									v-model="device.deviceIMEI"
									id="userDataMiddleName" 
									placeholder="Enter your device IMEI"
									type="text" 
									class="login__password"
									>
									<span class="material-symbols-outlined login__item_ico">barcode</span>
								</div>
								
							</div>

							<div v-if="device.deviceType !== 'single'" class="subDevice">
								<p class="form__title"><svg data-v-7b53e39e="" width="18" height="8" viewBox="0 0 18 8" xmlns="http://www.w3.org/2000/svg"><path data-v-7b53e39e="" d="M16 6.13492V5.05802C16 4.96822 15.9712 4.89452 15.9135 4.83682C15.8558 4.77912 15.782 4.75032 15.6923 4.75032H2.3077C2.21795 4.75032 2.14422 4.77912 2.08652 4.83682C2.02882 4.89452 1.99997 4.96822 1.99997 5.05802V6.13492H16ZM13.1884 3.25032L12.7922 2.06762C12.7666 2.00352 12.7281 1.95382 12.6768 1.91862C12.6256 1.88332 12.5679 1.86572 12.5038 1.86572H5.49612C5.43202 1.86572 5.37433 1.88332 5.32305 1.91862C5.27176 1.95382 5.2333 2.00352 5.20765 2.06762L4.8115 3.25032H13.1884ZM12.5038 0.365723C12.8922 0.365723 13.2439 0.478823 13.5587 0.705123C13.8734 0.931423 14.0942 1.23042 14.2211 1.60222L14.7691 3.25032H15.6923C16.191 3.25032 16.6169 3.42692 16.9701 3.78012C17.3234 4.13332 17.5 4.55932 17.5 5.05802V7.63492H0.5V5.05802C0.5 4.55932 0.67661 4.13332 1.02982 3.78012C1.38302 3.42692 1.80898 3.25032 2.3077 3.25032H3.23075L3.89038 1.54072C4.02756 1.19582 4.24679 0.913823 4.54808 0.694523C4.84934 0.475323 5.18908 0.365723 5.5673 0.365723H12.5038Z" fill="#64748B"></path></svg>Sub Device Info</p>
								<div class="modal__main_wrap">
									<div class="login__item">
										<label for="userDataEmail">Sub Device 2 ID</label>
										<input
										v-model="authStore.subscriber.id"
										id="userDataEmail" 
										placeholder="Sub Device 2 ID"
										type="email" 
										class="login__password"
										readonly
										>
										<span class="material-symbols-outlined login__item_ico">fingerprint</span>
									</div>
								</div>
							</div>

							<div class="modal__btn">
								<DefaultBtn class="btn btn--white" :neutral="true" text="Undo" @click="areYouSure" />
								<DefaultBtn type="submit" class="btn" :loading text="Save" />
							</div>
						</div>
				</form>
				</div>

				<div v-show="activeTab === 2" >
					<div class="modal__main modal__main-wrap">
						<div class="slider">
							<div class="slide" v-show="currentSlide === 0">
								<div class="modal__main_stat">
									<p class="form__title"><span class="material-symbols-outlined">notifications</span>Notification Info</p>
									<div class="modal__main_wrap">
										<div class="login__item">
											<label for="userDataName">Name</label>
											<input 
											v-model="device.notifications_name_1"
											id="userDataName" 
											placeholder="Name"
											type="text" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">person</span>
										</div>
										
										<div class="login__item">
											<label for="corporateId">Phone Number</label>
											<input 
											v-model="device.notifications_phone_1"
											id="corporateId" 
											placeholder="Phone Number"
											type="text" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">phone</span>
										</div>

										<div class="login__item login__item-last">
											<label for="userDataMiddleName">Email</label>
											<input 
											v-model="device.notifications_email_1"
											id="userDataMiddleName" 
											placeholder="Email"
											type="email" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">mail</span>
										</div>

										<div class="device__multy device__multy__btn">
											<div class="login__item login__item-last login__item-last_add">
												<button  v-if="!addInfo1" class="add__elem" type="button" @click="addContactInfo">
													<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
														<path d="M11.25 12.75H6.25C6.0375 12.75 5.85938 12.6781 5.71563 12.5343C5.57187 12.3904 5.5 12.2122 5.5 11.9997C5.5 11.7871 5.57187 11.609 5.71563 11.4654C5.85938 11.3218 6.0375 11.25 6.25 11.25H11.25V6.25C11.25 6.0375 11.3219 5.85938 11.4657 5.71563C11.6095 5.57187 11.7877 5.5 12.0003 5.5C12.2129 5.5 12.391 5.57187 12.5346 5.71563C12.6782 5.85938 12.7499 6.0375 12.7499 6.25V11.25H17.75C17.9625 11.25 18.1406 11.3219 18.2843 11.4657C18.4281 11.6095 18.5 11.7877 18.5 12.0003C18.5 12.2129 18.4281 12.391 18.2843 12.5346C18.1406 12.6782 17.9625 12.75 17.75 12.75H12.7499V17.75C12.7499 17.9625 12.6781 18.1406 12.5342 18.2843C12.3904 18.4281 12.2122 18.5 11.9997 18.5C11.787 18.5 11.609 18.4281 11.4654 18.2843C11.3218 18.1406 11.25 17.9625 11.25 17.75V12.75Z" fill="#2563EB"/>
													</svg>
													Add Contact Info 2
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>


						<div class="slide" v-show="currentSlide === 1">
							<div class="modal__main-add">
								<p class="form__title">
									<span class="material-symbols-outlined">notifications</span>Notification Info 2
								</p>
								<div class="modal__main_wrap">
										<div class="login__item">
											<label for="userDataName">Name</label>
											<input 
											v-model="device.notifications_name_2"
											id="userDataName"
											placeholder="Name"
											type="text" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">person</span>
										</div>
										
										<div class="login__item">
											<label for="corporateId">Phone Number</label>
											<input 
											v-model="device.notifications_phone_2"
											placeholder="Phone Number"
											id="corporateId" 
											type="text" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">phone</span>
										</div>

										<div class="login__item login__item-last">
											<label for="userDataMiddleName">Email</label>
											<input 
											v-model="device.notifications_email_2"
											id="userDataMiddleName" 
											placeholder="Email"
											type="email" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">mail</span>
										</div>
										<div class="login__item login__item-last login__item-last_add">
										<button   v-if="!addInfo2"   class="add__elem" type="button" @click="addContactInfo1">
											<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
												<path d="M11.25 12.75H6.25C6.0375 12.75 5.85938 12.6781 5.71563 12.5343C5.57187 12.3904 5.5 12.2122 5.5 11.9997C5.5 11.7871 5.57187 11.609 5.71563 11.4654C5.85938 11.3218 6.0375 11.25 6.25 11.25H11.25V6.25C11.25 6.0375 11.3219 5.85938 11.4657 5.71563C11.6095 5.57187 11.7877 5.5 12.0003 5.5C12.2129 5.5 12.391 5.57187 12.5346 5.71563C12.6782 5.85938 12.7499 6.0375 12.7499 6.25V11.25H17.75C17.9625 11.25 18.1406 11.3219 18.2843 11.4657C18.4281 11.6095 18.5 11.7877 18.5 12.0003C18.5 12.2129 18.4281 12.391 18.2843 12.5346C18.1406 12.6782 17.9625 12.75 17.75 12.75H12.7499V17.75C12.7499 17.9625 12.6781 18.1406 12.5342 18.2843C12.3904 18.4281 12.2122 18.5 11.9997 18.5C11.787 18.5 11.609 18.4281 11.4654 18.2843C11.3218 18.1406 11.25 17.9625 11.25 17.75V12.75Z" fill="#2563EB"/>
											</svg>
											Add Contact Info 3
										</button>
									</div>
								</div>
							</div>
						</div>

						<div class="slide" v-show="currentSlide === 2">
							<div class="modal__main-add">
								<p class="form__title">
									<span class="material-symbols-outlined">notifications</span>Notification Info 3
								</p>
								<div class="modal__main_wrap">
										<div class="login__item">
											<label for="userDataName">Name</label>
											<input 
											v-model="device.notifications_name_3"
											id="userDataName" 
											placeholder="Name"
											type="text" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">person</span>
										</div>
										
										<div class="login__item">
											<label for="corporateId">Phone Number</label>
											<input 
											v-model="device.notifications_phone_3"
											id="corporateId" 
											placeholder="Phone Number"
											type="text" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">phone</span>
										</div>

										<div class="login__item login__item-last">
											<label for="userDataMiddleName">Email</label>
											<input 
											v-model="device.notifications_email_3"
											id="userDataMiddleName" 
											placeholder="Email"
											type="email" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">mail</span>
										</div>
									</div>
							</div>
						</div>

						<div class="slider-controls"  v-if="totalSlides > 1">
							<span
								v-for="(slide, index) in totalSlides"
								:key="index"
								class="bullet"
								:class="{ active: currentSlide === index }"
								@click="goToSlide(index)"
							></span>
						</div>

							<!--<div class="device__multy">
								<div v-if="addInfo1 || device.notifications_name_2 || device.notifications_phone_2 || device.notifications_email_2 " class="modal__main-add">
									<p class="form__title"><span class="material-symbols-outlined">notifications</span>Notification Info 2</p>
									<div class="modal__main_wrap">
										<div class="login__item">
											<label for="userDataName">Name</label>
											<input 
											v-model="device.notifications_name_2"
											id="userDataName"
											placeholder="Name"
											type="text" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">person</span>
										</div>
										
										<div class="login__item">
											<label for="corporateId">Phone Number</label>
											<input 
											v-model="device.notifications_phone_2"
											placeholder="Phone Number"
											id="corporateId" 
											type="text" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">phone</span>
										</div>

										<div class="login__item login__item-last">
											<label for="userDataMiddleName">Email</label>
											<input 
											v-model="device.notifications_email_2"
											id="userDataMiddleName" 
											placeholder="Email"
											type="email" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">mail</span>
										</div>
										<div class="login__item login__item-last login__item-last_add">
										<button   v-if="!addInfo2"   class="add__elem" type="button" @click="addContactInfo1">
											<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
												<path d="M11.25 12.75H6.25C6.0375 12.75 5.85938 12.6781 5.71563 12.5343C5.57187 12.3904 5.5 12.2122 5.5 11.9997C5.5 11.7871 5.57187 11.609 5.71563 11.4654C5.85938 11.3218 6.0375 11.25 6.25 11.25H11.25V6.25C11.25 6.0375 11.3219 5.85938 11.4657 5.71563C11.6095 5.57187 11.7877 5.5 12.0003 5.5C12.2129 5.5 12.391 5.57187 12.5346 5.71563C12.6782 5.85938 12.7499 6.0375 12.7499 6.25V11.25H17.75C17.9625 11.25 18.1406 11.3219 18.2843 11.4657C18.4281 11.6095 18.5 11.7877 18.5 12.0003C18.5 12.2129 18.4281 12.391 18.2843 12.5346C18.1406 12.6782 17.9625 12.75 17.75 12.75H12.7499V17.75C12.7499 17.9625 12.6781 18.1406 12.5342 18.2843C12.3904 18.4281 12.2122 18.5 11.9997 18.5C11.787 18.5 11.609 18.4281 11.4654 18.2843C11.3218 18.1406 11.25 17.9625 11.25 17.75V12.75Z" fill="#2563EB"/>
											</svg>
											Add Contact Info 3
										</button>
									</div>
									</div>
								</div>

								<div v-if="addInfo2 || device.notifications_name_3 || device.notifications_phone_3 || device.notifications_email_3" class="modal__main-add">
									<p class="form__title"><span class="material-symbols-outlined">notifications</span>Notification Info 3</p>
									<div class="modal__main_wrap">
										<div class="login__item">
											<label for="userDataName">Name</label>
											<input 
											v-model="device.notifications_name_3"
											id="userDataName" 
											placeholder="Name"
											type="text" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">person</span>
										</div>
										
										<div class="login__item">
											<label for="corporateId">Phone Number</label>
											<input 
											v-model="device.notifications_phone_3"
											id="corporateId" 
											placeholder="Phone Number"
											type="text" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">phone</span>
										</div>

										<div class="login__item login__item-last">
											<label for="userDataMiddleName">Email</label>
											<input 
											v-model="device.notifications_email_3"
											id="userDataMiddleName" 
											placeholder="Email"
											type="email" 
											class="login__password">
											<span class="material-symbols-outlined login__item_ico">mail</span>
										</div>
									</div>
								</div>
							</div>-->
							<div class="modal__btn">
								<DefaultBtn class="btn btn--white" :neutral="true" text="Undo" @click="areYouSure" />
								<DefaultBtn type="submit" class="btn" :loading text="Save" />
							</div>
						</div>
				</div>
				
			</div>
		</div>

		<div v-if="modalStore.isModalVisible('DeviceInfoUpdated')" class="modal modal--profile modal--active">
			<div class="modal__body" >
				<div class="modal__header">
					<p class="modal__subtitle">
						<span class="material-symbols-outlined">edit</span>
						<span>Device Info Edit</span>
					</p>
					<button @click="modalStore.closeModal" class="modal__close">
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>
				<div class="modal__main modal__main-center">
					<svg class="modal__update_svg" width="93" height="94" viewBox="0 0 93 94" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M39.5532 57.505L28.1823 46.1341C27.5046 45.4564 26.6527 45.1096 25.6267 45.094C24.6007 45.0782 23.7331 45.425 23.024 46.1341C22.315 46.8432 21.9604 47.7029 21.9604 48.7132C21.9604 49.7234 22.315 50.5831 23.024 51.2922L36.4563 64.7245C37.3411 65.6093 38.3734 66.0517 39.5532 66.0517C40.733 66.0517 41.7652 65.6093 42.65 64.7245L69.8817 37.4928C70.5595 36.8151 70.9062 35.9633 70.9219 34.9373C70.9376 33.9112 70.5909 33.0437 69.8817 32.3346C69.1726 31.6255 68.313 31.271 67.3027 31.271C66.2924 31.271 65.4327 31.6255 64.7236 32.3346L39.5532 57.505ZM46.5082 93.5C40.0768 93.5 34.0316 92.2796 28.3727 89.8387C22.7136 87.3979 17.791 84.0854 13.605 79.9012C9.4188 75.7169 6.10477 70.7965 3.66286 65.1399C1.22096 59.4835 0 53.4396 0 47.0082C0 40.5768 1.22042 34.5316 3.66127 28.8727C6.10212 23.2136 9.41464 18.291 13.5988 14.105C17.7831 9.91881 22.7035 6.60478 28.3601 4.16287C34.0165 1.72096 40.0604 0.5 46.4918 0.5C52.9232 0.5 58.9684 1.72043 64.6273 4.16128C70.2864 6.60213 75.209 9.91464 79.395 14.0988C83.5812 18.2831 86.8952 23.2035 89.3371 28.8601C91.779 34.5165 93 40.5604 93 46.9918C93 53.4232 91.7796 59.4683 89.3387 65.1273C86.8979 70.7864 83.5854 75.709 79.4012 79.895C75.2169 84.0812 70.2965 87.3952 64.6399 89.8371C58.9835 92.279 52.9396 93.5 46.5082 93.5ZM46.5 86.158C57.4316 86.158 66.6908 82.3646 74.2777 74.7777C81.8646 67.1908 85.658 57.9316 85.658 47C85.658 36.0684 81.8646 26.8092 74.2777 19.2223C66.6908 11.6354 57.4316 7.842 46.5 7.842C35.5684 7.842 26.3092 11.6354 18.7223 19.2223C11.1354 26.8092 7.342 36.0684 7.342 47C7.342 57.9316 11.1354 67.1908 18.7223 74.7777C26.3092 82.3646 35.5684 86.158 46.5 86.158Z" fill="#2563EB"/>
					</svg>
					<p class="modal__update">Device information updated!</p>
				</div>
			</div>
		</div>

		<div v-if="modalStore.isModalVisible('DeviceInfoNoUpdated')" class="modal modal--profile modal--active">
			<div class="modal__body" >
				<div class="modal__header">
					<p class="modal__subtitle">
						<span class="material-symbols-outlined">edit</span>
						<span>Device Info Edit</span>
					</p>
					<button @click="modalStore.closeModal" class="modal__close">
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>
				<div class="modal__main modal__main-center">
					<svg class="modal__update_svg" width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M49.9429 79.0138C51.4181 79.0138 52.6642 78.5045 53.681 77.4859C54.6977 76.4673 55.206 75.2204 55.206 73.7451C55.206 72.2699 54.6967 71.0239 53.6781 70.0072C52.6595 68.9904 51.4126 68.482 49.9373 68.482C48.4621 68.482 47.2161 68.9913 46.1994 70.0099C45.1826 71.0285 44.6742 72.2754 44.6742 73.7507C44.6742 75.2259 45.1835 76.472 46.2021 77.4888C47.2207 78.5055 48.4676 79.0138 49.9429 79.0138ZM50.0084 99.0844C43.2197 99.0844 36.8387 97.7962 30.8653 95.2197C24.8919 92.6433 19.6959 89.1467 15.2773 84.7301C10.8585 80.3134 7.36041 75.1196 4.78285 69.1488C2.20529 63.1782 0.916504 56.7985 0.916504 50.0098C0.916504 43.2212 2.20473 36.8402 4.78117 30.8668C7.35762 24.8934 10.8542 19.6973 15.2708 15.2787C19.6875 10.86 24.8813 7.36188 30.852 4.78432C36.8227 2.20676 43.2024 0.917969 49.9911 0.917969C56.7797 0.917969 63.1607 2.2062 69.1341 4.78264C75.1075 7.35909 80.3036 10.8556 84.7222 15.2723C89.1409 19.689 92.639 24.8827 95.2166 30.8535C97.7941 36.8242 99.0829 43.2039 99.0829 49.9925C99.0829 56.7812 97.7947 63.1622 95.2183 69.1355C92.6418 75.109 89.1453 80.305 84.7286 84.7236C80.3119 89.1423 75.1182 92.6405 69.1474 95.218C63.1767 97.7956 56.797 99.0844 50.0084 99.0844ZM49.9997 91.3345C61.5386 91.3345 71.3122 87.3304 79.3205 79.322C87.3289 71.3137 91.3331 61.5401 91.3331 50.0012C91.3331 38.4623 87.3289 28.6887 79.3205 20.6803C71.3122 12.672 61.5386 8.66784 49.9997 8.66784C38.4608 8.66784 28.6872 12.672 20.6789 20.6803C12.6705 28.6887 8.66638 38.4623 8.66638 50.0012C8.66638 61.5401 12.6705 71.3137 20.6789 79.322C28.6872 87.3304 38.4608 91.3345 49.9997 91.3345ZM50.4182 27.8838C52.802 27.8838 54.865 28.6343 56.6071 30.1351C58.3492 31.6359 59.2203 33.5119 59.2203 35.7631C59.2203 37.6576 58.6639 39.3533 57.5511 40.8502C56.4382 42.3473 55.1665 43.7416 53.7357 45.0333C51.8602 46.6971 50.209 48.5272 48.7819 50.5238C47.355 52.5203 46.5918 54.744 46.4924 57.1949C46.4593 58.1354 46.7905 58.9236 47.486 59.5595C48.1815 60.1954 48.9929 60.5133 49.9203 60.5133C50.9138 60.5133 51.755 60.1821 52.4439 59.5197C53.1327 58.8574 53.5732 58.0459 53.7653 57.0855C54.1098 55.3103 54.8434 53.7288 55.9662 52.3412C57.0889 50.9534 58.3052 49.6338 59.615 48.3825C61.4979 46.5471 63.1415 44.5459 64.5457 42.3788C65.95 40.2116 66.6521 37.7945 66.6521 35.1272C66.6521 31.0337 65.0392 27.6704 61.8134 25.0374C58.5876 22.4044 54.8219 21.0879 50.5164 21.0879C47.4429 21.0879 44.545 21.7685 41.8226 23.1297C39.1002 24.4909 36.9805 26.4764 35.4636 29.0863C34.9933 29.8877 34.8423 30.7313 35.0107 31.617C35.179 32.5027 35.6336 33.1752 36.3743 33.6345C37.3171 34.1593 38.2853 34.3091 39.2789 34.0838C40.2724 33.8587 41.117 33.2924 41.8125 32.3849C42.8591 31.0269 44.1259 29.9373 45.613 29.116C47.1 28.2945 48.7018 27.8838 50.4182 27.8838Z" fill="#FF3F6D"/>
					</svg>

					<p class="modal__update">Are you sure?</p>
					<p class="modal__update_text">You are about to close this page without saving.</p>
					<div class="modal__update_btn">
						<button class="btn btn--white btn--border" type="button" @click="modalStore.closeModal">Close</button>
						<button class="btn" type="submit" @click="modalStore.openModal('DeviceInfoEdit')">Continue</button>
					</div>
				</div>
			</div>
		</div>
	</div>

</template>

<style scoped>
	.modal__main{
		overflow: auto;
		max-height: 700px;
		justify-content: start;
	}

	.modal__main-center{
		justify-content: center;
	}

	.modal__main-add{
		padding-top: 32px;
		margin-top: 32px;
		border-top: 1px solid #DBEAFE;
	}

	.add__elem{
		display: flex;
		align-items: center;
		background: none;
		border: none;
		outline: none;
		cursor: pointer;
		color: #2563EB;
		font-weight: 600;
		margin-top: 15px;
	}

	.btn--white{
		border: 1px solid #E2E8F0
	}
	.btn{
		box-shadow: none;
	}

	.form__title{
		display: flex;
		align-items: center;
		gap: 10px;
		color: #2563EB;
		font-weight: 600;
		width: 333px;
		margin: 0 auto 20px 20px; 
	}
	.form__title path{
		fill: #2563EB;
	}
	.modal__btn{
		margin-top: 50px;
	}
	.modal__main{
		padding-top: 25px;
	}
	.device__multy__btn{
		width: 333px;
		margin: 0 auto;
	}

	.login__item-last{
		margin: 0 auto 25px;
	}
	.login__item-last_add{
		margin: 0 auto;
		width: 333px;
	}
	.login__item_ico{
		top: 63%;
	}

	.modal__tabs{
		display: flex;
		justify-content: center;
		gap: 50px;
		padding: 30px 0 20px 0; 
	}

	.modal__tabs a{
		position: relative;
		color: #64748B;
		font-weight: 500;
		font-size: 14px;
	}

	::v-deep .deviceInfo__tabs_active:before{
		position: absolute;
		content: '';
		background: #2563EB;
		left: 50%;
		bottom: -10px;
		transform: translate(-50% , 0);
		width: 100%;
		height: 4px;
		border-top-left-radius: 10px;
		border-top-right-radius: 10px; 
		opacity: 1;
		transition: all .6s;
	}

	.slider-controls {
		display: flex;
		justify-content: center;
		margin-top: 20px;
	}

	.bullet {
		width: 12px;
		height: 12px;
		background-color: #ccc;
		border-radius: 50%;
		margin: 0 6px;
		cursor: pointer;
		transition: background-color 0.3s;
	}

	.bullet.active {
		background-color: #2563EB;
	}
	
	@media (max-width: 1440px) {
		.form__title{
			text-align: center;
			margin: 0 auto 20px;
			width: 333px;
		}
		.login__item-last{
			margin: 0 auto !important;
		}
		.modal__main_wrap{
			flex-direction: column;
		}
		.modal__btn{
			margin: 50px auto 0;
		}
	}
	@media (max-width: 992px) {
		.modal--profile .modal__body{
			max-width: 90%;
			margin: 0 auto;
		}
		.deviceInfo__tabs_active {
			color: #2563EB !important;
		}
		.modal__main-add{
			padding: 0;
			border: none;
		}
	}

	@media (max-width: 676px) {
		.modal--profile .modal__body{
			max-width: 100vw;
		}
	}

	@media (max-width: 480px) {
		.modal__main{
			padding: 0;
		}
		.modal__main-wrap{
			padding-bottom: 20px;
		}
	}
	

</style>